/** Client MCP **Knowledge gaps** (client MCP plan §12.6 #6 "a feedback loop"; backend WPS P2-10):
 *  what clients' AI could not resolve, from daily counters — words `lookup_terms` matched to
 *  nothing, analytics variants it had to ask for, metrics, breakdowns and periods the catalog
 *  lacks, and the same person re-asking a metric with another variant (a misunderstanding).
 *
 *  `GET /knowledge_gaps/` (`console_knowledge_gaps_view`, Ops Admin by default):
 *  - `since` / `until` — UTC days, both included; by default the 30 days up to today, **at most
 *    92** (a longer window is a 400). The answer names the window it used.
 *  - `tour_operator_id` — tenant ids, or `system` alone for calls without a tenant (an ops user's).
 *  - `kind` (a `KnowledgeGapKindKey` or a comma list), `search` (part of the subject or the
 *    detail, ≤ 100 characters), `group` (`subject` across tenants with `tenants`; `tenant`; `day`
 *    as counted), `ordering` (`count`, `last_at`, `subject`, `kind`, plus `tenants` with group
 *    `subject` and `day` with group `day`; `-` for descending; default `-count`).
 *  - At most 1000 rows (`truncated`).
 *
 *  Subjects are clients' AI's words, stored normalised (one case, punctuation folded, at most five
 *  words and 64 characters) and scanned first: personal data, a secret or a sentence is never
 *  stored — a placeholder counts it (`withheld`). Shapes come from the generated spec types; this
 *  module adds the query keys and the words. */
import { apiGet, extendedListConfig } from './client';
import type { components } from './types';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type KnowledgeGaps = Schemas['KnowledgeGaps'];
export type KnowledgeGapRow = Schemas['KnowledgeGapRow'];
export type KnowledgeGapKind = Schemas['KnowledgeGapKind'];
export type KnowledgeGapKindKey = Schemas['KnowledgeGapKindKey'];
export type KnowledgeGapGroup = KnowledgeGaps['group'];
export type KnowledgeGapWithheld = NonNullable<KnowledgeGapRow['withheld']>;

/** The longest window, in UTC days (both ends included). */
export const MAX_WINDOW_DAYS = 92;
/** The longest search the backend takes. */
export const MAX_SEARCH = 100;

export interface KnowledgeGapsParams {
  /** First UTC day, `YYYY-MM-DD`. */
  since?: string;
  /** Last UTC day (included), `YYYY-MM-DD`. */
  until?: string;
  /** Tenant ids, or `system`. */
  tour_operator_id?: TenantIdsParam | 'system';
  /** A kind or a comma-separated list. */
  kind?: string;
  search?: string;
  group?: KnowledgeGapGroup;
  ordering?: string;
  timeout?: 'extended';
}

type QueryValue = string | number | boolean | null | undefined;

function toQuery(params: Record<string, QueryValue>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function knowledgeGaps(params: KnowledgeGapsParams = {}): Promise<KnowledgeGaps> {
  return apiGet(`/knowledge_gaps/${toQuery({ ...params })}`, extendedListConfig(params.timeout));
}

export const knowledgeGapKeys = {
  all: ['knowledgeGaps'] as const,
  gaps: (params: KnowledgeGapsParams) => ['knowledgeGaps', params] as const,
};

// ---- words -----------------------------------------------------------------------

/** Every kind, in the order the filter offers them before the backend's own labels arrive
 *  (the contract test keeps it equal to `KnowledgeGapKindKey`). */
export const KNOWLEDGE_GAP_KINDS = [
  'lookup_miss',
  'needs_clarification',
  'unavailable_metric',
  'unavailable_dimension',
  'unavailable_grain',
  'variant_reask',
] as const satisfies readonly KnowledgeGapKindKey[];

export const GROUP_LABELS: Record<KnowledgeGapGroup, string> = {
  subject: 'By subject',
  tenant: 'By tenant',
  day: 'By day',
};

/** What a placeholder subject stands for (ours, never theirs). */
export const WITHHELD_LABELS: Record<KnowledgeGapWithheld, string> = {
  personal_or_secret: 'Withheld: personal data or a secret',
  sentence: 'Withheld: a sentence, not a term',
  over_cap: 'Withheld: over the daily cap of subjects',
};

/** The sortable columns per group, by their `ordering` key (the grid field is the same name). */
export const ORDERING_KEYS: Record<KnowledgeGapGroup, readonly string[]> = {
  subject: ['count', 'last_at', 'subject', 'kind', 'tenants'],
  tenant: ['count', 'last_at', 'subject', 'kind'],
  day: ['count', 'last_at', 'subject', 'kind', 'day'],
};

/** `ordering` as the backend takes it for `group`, or undefined (its default, `-count`). */
export function orderingFor(group: KnowledgeGapGroup, raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const key = raw.replace(/^-/, '');
  return ORDERING_KEYS[group].includes(key) ? raw : undefined;
}

/** `group` from a hand-editable URL value. */
export function groupOf(raw: string | null | undefined): KnowledgeGapGroup {
  return raw === 'tenant' || raw === 'day' ? raw : 'subject';
}
